import * as React from 'react';
import classNames from 'classnames';
import { chatEventEmitter, ChatRoom, Message } from '@wix/chat-sdk';
import {
  AVAILABILITY_STATUS,
  Avatar as IAvatar,
  AvatarType,
  expandCollapseManager,
  ImageAvatar,
  isNoneAvatar,
  MessageNotification,
  NoneAvatar,
  ThemeProvider,
  ISendersData,
} from '@wix/chat-web';
import styles from './MessagePopup.scss';
import wix from '../../services/wix';
import _get from 'lodash/get';
import Avatar from '../Avatar/Avatar';
import { connect } from 'react-redux';
import {
  editorSettingsAvatar,
  getLocalizedTexts,
  theme,
  isExperimentOn,
} from '../../reducers';
import { updateUnreadCount } from '../../actions';
import { AVATARS } from '../../constants/avatars';
import { lazyLoaders } from '../../services/loading-scheduler';
import { Experiments } from '../../constants/experiments';
import { withExperiments } from '@wix/wix-experiments-react';

export interface MessagePopupProps {
  theme: any;
  locale: string;
  editorSettingsLayout: any;
  deviceType: string;
  editorSettingsAvatar: any;
  widgetExpanded: boolean;
  unreadCount: number;
  businessChatRoomId: string;
  activeRoom?: string;
  updateUnreadCount(unreadCount: number): void;
  onClick: Function;
  isResponsive: boolean;
  isResponsiveExperimentOn: boolean;
  widgetSenderData: ISendersData;
  availabilityStatus: any;
  localizedTexts: any;
  experiments: any;
}

interface MessagePopupState {
  newMessages: Message[];
  shouldShowNotification: boolean;
  lastMessageChatroom?: ChatRoom;
  isMobileResponsive: boolean;
}

class _MessagePopup extends React.Component<
  MessagePopupProps,
  MessagePopupState
> {
  readonly state: MessagePopupState = {
    newMessages: [] as Message[],
    shouldShowNotification: false,
    lastMessageChatroom: undefined,
    isMobileResponsive: false,
  };

  componentDidMount() {
    const { deviceType, isResponsive, isResponsiveExperimentOn } = this.props;

    const isDesktop = deviceType === 'desktop';
    const isMobileResponsive =
      deviceType === 'mobile' && isResponsive && isResponsiveExperimentOn;

    this.setState({ isMobileResponsive });

    if (!isDesktop && !isMobileResponsive) {
      return;
    }

    this.subscribeToNewMessages();
    this.handleCollapseExpand();
  }

  onExpand = () => {
    this.setState({
      newMessages: [],
      shouldShowNotification: false,
      lastMessageChatroom: undefined,
    });
    this.props.updateUnreadCount(0);
  };

  componentWillUnmount() {
    this.unsubscribeFromNewMessages();

    expandCollapseManager.unregisterOnExpand(this.onExpand);
  }

  handleCollapseExpand() {
    expandCollapseManager.registerOnExpand(this.onExpand);
  }

  onNewMessage = async (messageDto) => {
    const message = new Message(messageDto);

    if (
      this.state.newMessages.some(
        (aMessage: Message) => aMessage.id === message.id,
      )
    ) {
      return;
    }

    const isSameRoom =
      !this.props.activeRoom || this.props.activeRoom === message.chatroomId;

    if (this.props.widgetExpanded && isSameRoom) {
      return;
    }

    const lazySdk = await lazyLoaders.interactivity.chatSdk();

    const chatroomId = message.chatroomId;
    let lastMessageChatroom;
    lastMessageChatroom = await lazySdk.chatSdk.getChatroom({
      chatroomId,
    });

    const newMessages = [...this.state.newMessages, message];

    this.setState({
      newMessages,
      shouldShowNotification: true,
      lastMessageChatroom,
    });
    if (this.props.unreadCount > 0) {
      this.props.updateUnreadCount(newMessages.length);
    }

    try {
      if (this.props.widgetExpanded) {
        await wix.resizeToExpandedWithMessageNotification();
      } else {
        await wix.resizeToCollapsedWithMessageNotification();
      }
    } catch (e) {}
  };

  subscribeToNewMessages() {
    chatEventEmitter.addListener(
      chatEventEmitter.CHAT_EVENTS.NEW_REALTIME_MESSAGE,
      this.onNewMessage,
    );
  }

  unsubscribeFromNewMessages() {
    chatEventEmitter.removeListener(
      chatEventEmitter.CHAT_EVENTS.NEW_REALTIME_MESSAGE,
      this.onNewMessage,
    );
  }

  onClick = () => {
    const lastMessage =
      this.state.newMessages[this.state.newMessages.length - 1];
    this.props.onClick && this.props.onClick(lastMessage);

    this.setState({
      newMessages: [],
      shouldShowNotification: false,
      lastMessageChatroom: undefined,
    });
  };

  onClose = () => {
    const { newMessages } = this.state;

    if (!this.props.unreadCount) {
      this.props.updateUnreadCount(newMessages.length);
    }

    this.setState({ shouldShowNotification: false });

    const asRounded =
      _get(this.props, 'editorSettingsLayout.collapsedAsRounded', false) ||
      (wix.isMobileDevice() && this.props.isResponsive);

    if (this.props.widgetExpanded) {
      void wix.resizeToExpandedMode();
    } else {
      void wix.resizeToCollapsedMode(asRounded);
    }
  };

  getAvatar = (): IAvatar | undefined => {
    const { newMessages } = this.state;
    const { lastMessageChatroom } = this.state;

    const lastMessage = newMessages[newMessages.length - 1];

    if (this.isBusinessRoom()) {
      if (!isNoneAvatar(this.props.editorSettingsAvatar)) {
        return this.props.editorSettingsAvatar;
      }
      if (
        lastMessage.sender &&
        lastMessage.sender.type === 'contact' &&
        lastMessage.senderDisplayData &&
        lastMessage.senderDisplayData.image &&
        lastMessage.senderDisplayData.image.url
      ) {
        return {
          avatarType: AvatarType.Image,
          imgUrl: lastMessage.senderDisplayData.image.url,
        } as ImageAvatar;
      }
      return {
        avatarType: AvatarType.None,
      } as NoneAvatar;
    }

    const imgUrl = lastMessageChatroom?.thumbnail || '';

    if (imgUrl) {
      return {
        avatarType: AvatarType.Image,
        imgUrl,
      } as ImageAvatar;
    }

    return AVATARS.DEFAULT as any;
  };

  isBusinessRoom = () => {
    const { newMessages } = this.state;
    const { businessChatRoomId } = this.props;
    const lastMessage = newMessages[newMessages.length - 1];

    return businessChatRoomId === lastMessage.chatroomId;
  };

  getBusinessTitle = () => {
    const { localizedTexts, availabilityStatus } = this.props;
    const { onlineTitle, offlineTitle } = localizedTexts;
    const isAvailable = availabilityStatus !== AVAILABILITY_STATUS.AWAY;

    return isAvailable ? onlineTitle : offlineTitle;
  };

  getTitle = () => {
    const { isMobileResponsive, lastMessageChatroom, newMessages } = this.state;
    const isPrivateRoom = lastMessageChatroom?.isPrivate;
    const lastMessage = newMessages[newMessages.length - 1];
    const chatroomName = lastMessageChatroom?.name || '';
    const lastSenderName = lastMessage?.senderDisplayData?.name;
    const groupChatInMobileResponsive = !isPrivateRoom && isMobileResponsive;

    if (isPrivateRoom && isMobileResponsive) {
      return;
    }

    if (this.isBusinessRoom()) {
      return this.getBusinessTitle();
    }

    if (isPrivateRoom) {
      return chatroomName;
    }

    if (groupChatInMobileResponsive) {
      return chatroomName;
    }

    return `${lastSenderName}@${chatroomName}`;
  };

  render() {
    const {
      newMessages,
      shouldShowNotification,
      isMobileResponsive,
      lastMessageChatroom,
    } = this.state;

    if (!shouldShowNotification) {
      return null;
    }

    const avatar = this.getAvatar();
    const asRounded = _get(
      this.props.editorSettingsLayout,
      'collapsedAsRounded',
      false,
    );

    const isExpanded = this.props.widgetExpanded;
    const lastMessage = newMessages[newMessages.length - 1];
    const isSameRoom =
      !this.props.activeRoom ||
      this.props.activeRoom === lastMessage.chatroomId;
    const isCompact = isExpanded && !isSameRoom;
    const isPrivate = lastMessageChatroom?.isPrivate;

    return (
      <div
        data-hook="message-popup"
        className={classNames(styles.messagePopup, {
          [styles.asRounded]: asRounded,
          [styles.isExpanded]: isExpanded,
        })}
      >
        <ThemeProvider theme={this.props.theme}>
          <MessageNotification
            messages={isCompact ? [lastMessage] : newMessages}
            onClick={() => this.onClick()}
            onClose={() => this.onClose()}
            avatar={avatar ? <Avatar size={30} avatar={avatar} /> : undefined}
            title={this.getTitle()}
            withCallToAction={!isCompact}
            locale={this.props.locale}
            mobileResponsive={isMobileResponsive}
            isPrivate={isPrivate}
            sendersData={this.props.widgetSenderData}
          />
        </ThemeProvider>
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  theme: theme(state),
  editorSettingsAvatar: editorSettingsAvatar(state),
  locale: state.locale,
  deviceType: state.deviceType,
  localizedTexts: getLocalizedTexts(state),
  editorSettingsLayout: state.editorSettingsLayout,
  widgetExpanded: state.widgetExpanded,
  unreadCount: state.unreadCount,
  businessChatRoomId: state.businessChatRoomId,
  isResponsiveExperimentOn: isExperimentOn(
    state,
    Experiments.MobileResponsiveNotifications,
  ),
  widgetSenderData: state.widgetSenderData,
  availabilityStatus: state.availabilityStatus,
});

const mapDispatchToProps = (dispatch) => ({
  updateUnreadCount: (unreadCount) => dispatch(updateUnreadCount(unreadCount)),
});

export const MessagePopup = connect(
  mapStateToProps,
  mapDispatchToProps,
)(withExperiments(_MessagePopup));
